import { syncLogContext, syncLogScalars } from '@/lib/local-db/sync-log-fields';

import type { Metadata, RingLine } from '@littlebird/logger';
import type { SyncStatus } from '@littlebird/sdk';

const yesNo = (value: boolean) => (value ? 'yes' : 'no');

export function formatSyncSection(status: SyncStatus | null): string {
  if (!status) return '';
  const lines = [
    '\n\n=== Sync ===',
    `- Phase: ${status.phase} (${status.lastBootstrapMode}), network failures: ${status.consecutiveNetworkFailures}`,
    `- Connection: ws=${status.wsConnected ? 'connected' : 'disconnected'} uploading=${yesNo(status.uploading)} downloading=${yesNo(status.downloading)} hasSynced=${yesNo(status.hasSynced)} stale=${status.staleForMs === undefined ? 'n/a' : `${Math.round(status.staleForMs / 1000)}s`} errors download=${status.downloadErrors} upload=${status.uploadErrors}`,
    `- lastSyncId: ${status.lastSyncId} (applied ${status.lastAppliedSyncId}) at ${status.lastSyncTime ?? 'never'}${status.cursorHeldForCycles > 0 ? `, held for ${status.cursorHeldForCycles} cycle(s)` : ''}`,
    `- Last sync request id: ${status.lastSyncRequestId ?? 'none'} (prod-traces: sync.request_id)`,
    `- Queue: pending=${status.queue.pending} executing=${status.queue.executing} awaitingSync=${status.queue.awaitingSync}`,
    ...status.queue.transactions.map(
      (tx) =>
        `  ${tx.id} ${tx.table} ${tx.op} ${tx.modelId} ${tx.state} attempt=${tx.attempt} created=${tx.createdAt}` +
        (tx.syncIdNeededForCompletion !== undefined
          ? ` needsSyncId=${tx.syncIdNeededForCompletion}`
          : '')
    ),
  ];
  if (status.lastPacketError)
    lines.push(
      `- Last packet error: ${status.lastPacketError.table} syncId=${status.lastPacketError.syncId ?? 'n/a'} ${status.lastPacketError.message} at ${status.lastPacketError.at}`
    );
  if (status.degraded.length > 0)
    lines.push(`- Degraded: ${status.degraded.join('; ')}`);
  return lines.join('\n');
}

export function formatUrlSection(title: string, urls: string[]) {
  if (urls.length === 0) return '';
  return `\n\n=== ${title} ===\n${urls.map((url, index) => `${index + 1}. ${url}`).join('\n')}`;
}

export function formatInfoSection() {
  return `\n\n=== Info ===\n- App version: ${__APP_VERSION__}\n`;
}

/** Same fields the Axiom transport ships: sync-engine lines only, scalars only */
export function formatSyncLogsSection(lines: RingLine[]): string {
  const syncLines = lines.filter((line) =>
    syncLogContext(line.metadata as Metadata | undefined)
  );
  if (syncLines.length === 0) return '';
  return [
    `\n\n=== Sync Logs (${syncLines.length}) ===`,
    ...syncLines.map((line) => {
      const fields = syncLogScalars(line.metadata as Metadata | undefined);
      return (
        `${new Date(line.at).toISOString()} ${line.level}${line.context ? ` (${line.context})` : ''} ${line.message}` +
        (Object.keys(fields).length > 0 ? ` ${JSON.stringify(fields)}` : '')
      );
    }),
  ].join('\n');
}
